//frontend/dispatcher-client/src/components/personnel/PersonnelLayout.jsx
import React, {
  useEffect,
} from "react";

import {
  NavLink,
  Outlet,
  useLocation,
} from "react-router-dom";

import {
  LuCalendarDays,
  LuClipboardCheck,
  LuCalendarRange,
  LuCoins,
} from "react-icons/lu";

import {
  FiGrid,
} from "react-icons/fi";

import {
  useTranslation,
} from "react-i18next";

import {
  prefetchPersonnelFoundation,
  getPersonnelPrefetchScopeFromPath,
} from "../../lib/prefetchPersonnel";

import usePersonnelPermissions from "../../hooks/usePersonnelPermissions";
import usePersonnelPendingCount from "../../hooks/usePersonnelPendingCount";

import {
  getSocket,
} from "../../lib/socket";

import {
  getPersonnelCompanyId,
  markPersonnelResourceStale,
} from "../../utils/personnelCache";

import "../../styles/Personnel.css";

const NAV_ITEMS = [
  {
    to:
      "/personnel/dashboard",

    key:
      "dashboard",

    /*
     * Dashboard and Calendar consume the same
     * Personnel visibility data for now.
     */
    permission:
      "personnelCalendarView",

    icon:
      FiGrid,
  },

  {
    to:
      "/personnel/calendar",

    key:
      "calendar",

    permission:
      "personnelCalendarView",

    icon:
      LuCalendarDays,
  },

  {
    to:
      "/personnel/requests",

    key:
      "requests",

    permission:
      "personnelRequestsView",

    icon:
      LuClipboardCheck,
  },

  {
    to:
      "/personnel/public-holidays",

    key:
      "public_holidays",

    permission:
      "personnelPublicHolidaysView",

    icon:
      LuCalendarRange,
  },

  {
    to:
      "/personnel/compensation",

    key:
      "compensation",

    permission:
      "personnelCompensationView",

    icon:
      LuCoins,
  },
];

export default function PersonnelLayout() {
  const {
    t,
  } =
    useTranslation(
      "personnel"
    );

  const location =
    useLocation();

  const {
    can,
  } =
    usePersonnelPermissions();

  const canViewRequests =
    can(
      "personnelRequestsView"
    );

  const pendingRequestCount =
    usePersonnelPendingCount({
      enabled:
        canViewRequests,

      /*
       * Sidebar owns the global WS listener.
       * Here we only consume its shared cache.
       */
      listenRealtime:
        false,
    });

  const visibleNavItems =
    NAV_ITEMS.filter(
      (
        item
      ) =>
        can(
          item.permission
        )
    );

  useEffect(
    () => {
      prefetchPersonnelFoundation({
        scope:
          getPersonnelPrefetchScopeFromPath(
            location.pathname
          ),
      })
        .catch(
          (
            error
          ) => {
            console.warn(
              "[Personnel] prefetch failed:",
              error
            );
          }
        );
    },
    [
      location.pathname,
    ]
  );

  useEffect(
    () => {
      const socket =
        getSocket();

      const handlePersonnelChanged =
        (
          payload = {}
        ) => {
          const currentCompanyId =
            getPersonnelCompanyId();

          const eventCompanyId =
            Number(
              payload.companyId
            );

          /*
           * Extra tenant guard.
           *
           * The backend already emits only to:
           *
           * company:${companyId}
           *
           * but we still refuse events whose
           * payload does not match the current company.
           */
          if (
            !currentCompanyId ||
            !Number.isInteger(
              eventCompanyId
            ) ||
            eventCompanyId !==
              currentCompanyId
          ) {
            return;
          }

          const resources =
            Array.isArray(
              payload.resources
            )
              ? payload.resources
              : [];

          const allowedResources =
            new Set([
              "people",
              "absenceRequests",
              "absences",
              "publicHolidays",
              "holidayWork",
              "workDays",
            ]);

          resources.forEach(
            (
              resource
            ) => {
              if (
                !allowedResources.has(
                  resource
                )
              ) {
                return;
              }

              markPersonnelResourceStale(
                resource
              );
            }
          );

          console.log(
            "[Personnel] realtime change",
            {
              entity:
                payload.entity ||
                null,

              action:
                payload.action ||
                null,

              resources,
            }
          );
        };

      socket.on(
        "personnel:changed",
        handlePersonnelChanged
      );

      return () => {
        socket.off(
          "personnel:changed",
          handlePersonnelChanged
        );
      };
    },
    []
  );

  return (
    <div className="personnel-shell">
      <nav className="personnel-nav">
        {visibleNavItems.map(
          (
            item
          ) => {
            const Icon =
              item.icon;

            return (
              <NavLink
                key={
                  item.key
                }
                to={
                  item.to
                }
                className={({
                  isActive,
                }) =>
                  [
                    "personnel-nav-item",

                    isActive
                      ? "is-active"
                      : "",
                  ]
                    .filter(
                      Boolean
                    )
                    .join(
                      " "
                    )
                }
              >
                <Icon />

                <span>
                  {t(
                    `navigation.${item.key}`
                  )}
                </span>

                {item.key ===
                  "requests" &&
                  pendingRequestCount >
                    0 && (
                    <span
                      className="personnel-nav-badge"
                      aria-hidden="true"
                    >
                      {pendingRequestCount >
                      99
                        ? "99+"
                        : pendingRequestCount}
                    </span>
                  )}
              </NavLink>
            );
          }
        )}
      </nav>

      <main className="personnel-content">
        <Outlet />
      </main>
    </div>
  );
}